/**
 * Roblox-inspired blocky style: everything is bevelled boxes and cylinders with flat,
 * saturated colours. No textures except a tiny generated stud pattern for the baseplate.
 */
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import type { VehicleDef } from '@typing-racer/shared';
import type { BuiltVehicle, StyleRenderer } from '../style.js';

const HEAD = '#f5d142';
const TORSO = '#1565c0';
const DARK = '#263238';
const GLASS = '#90caf9';
const LIGHT = '#fff59d';
const TAIL = '#ef5350';

const geoCache = new Map<string, THREE.BufferGeometry>();
function boxGeo(w: number, h: number, d: number, r = 0.08): THREE.BufferGeometry {
  const k = `${w},${h},${d},${r}`;
  let g = geoCache.get(k);
  if (!g) {
    const radius = Math.min(r, w / 2, h / 2, d / 2);
    g = radius > 0 ? new RoundedBoxGeometry(w, h, d, 2, radius) : new THREE.BoxGeometry(w, h, d);
    geoCache.set(k, g);
  }
  return g;
}
const matCache = new Map<string, THREE.MeshLambertMaterial>();
export function mat(hex: string): THREE.MeshLambertMaterial {
  let m = matCache.get(hex);
  if (!m) {
    m = new THREE.MeshLambertMaterial({ color: new THREE.Color(hex) });
    matCache.set(hex, m);
  }
  return m;
}

export function box(
  w: number,
  h: number,
  d: number,
  colour: string,
  x = 0,
  y = 0,
  z = 0,
  r = 0.08,
): THREE.Mesh {
  const m = new THREE.Mesh(boxGeo(w, h, d, r), mat(colour));
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

function wheel(radius: number, width: number, x: number, y: number, z: number): THREE.Group {
  const pivot = new THREE.Group();
  pivot.position.set(x, y, z);
  const tyre = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, width, 12), mat(DARK));
  tyre.rotation.z = Math.PI / 2;
  tyre.castShadow = true;
  const hub = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.55, radius * 0.55, width + 0.04, 8), mat('#b0bec5'));
  hub.rotation.z = Math.PI / 2;
  pivot.add(tyre, hub);
  return pivot;
}

/** Classic blocky avatar: yellow head and arms, blue torso. Sits with hands forward. */
export function driver(scale = 1): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.7, 0.7, 0.7, HEAD, 0, 1.15, 0, 0.12));
  // face
  g.add(box(0.12, 0.12, 0.05, DARK, -0.16, 1.22, 0.36, 0));
  g.add(box(0.12, 0.12, 0.05, DARK, 0.16, 1.22, 0.36, 0));
  g.add(box(0.3, 0.06, 0.05, DARK, 0, 1.02, 0.36, 0));
  g.add(box(0.8, 0.8, 0.45, TORSO, 0, 0.4, 0, 0.06));
  const armL = box(0.25, 0.25, 0.7, HEAD, -0.52, 0.55, 0.25, 0.06);
  const armR = box(0.25, 0.25, 0.7, HEAD, 0.52, 0.55, 0.25, 0.06);
  armL.rotation.x = -0.4;
  armR.rotation.x = -0.4;
  g.add(armL, armR);
  g.scale.setScalar(scale);
  return g;
}

function lights(g: THREE.Group, L: number, W: number, y: number): void {
  g.add(box(0.3, 0.2, 0.1, LIGHT, -W / 2 + 0.35, y, L / 2 + 0.02, 0.02));
  g.add(box(0.3, 0.2, 0.1, LIGHT, W / 2 - 0.35, y, L / 2 + 0.02, 0.02));
  g.add(box(0.3, 0.16, 0.1, TAIL, -W / 2 + 0.35, y, -L / 2 - 0.02, 0.02));
  g.add(box(0.3, 0.16, 0.1, TAIL, W / 2 - 0.35, y, -L / 2 - 0.02, 0.02));
}

/** Vehicle faces +Z. Wheels returned as pivots to spin around X. */
export function buildBlockyVehicle(def: VehicleDef, colour: string): BuiltVehicle {
  const g = new THREE.Group();
  const wheels: THREE.Object3D[] = [];
  const L = def.length;
  const W = def.width;
  let r = 0.45; // wheel radius
  let ww = 0.35;

  const addWheels = (radius: number, width: number, zs: number[], y = radius, xOff = W / 2) => {
    for (const z of zs) {
      for (const x of [-xOff, xOff]) {
        const p = wheel(radius, width, x, y, z);
        g.add(p);
        wheels.push(p);
      }
    }
  };

  switch (def.id) {
    case 'race': {
      r = 0.42;
      g.add(box(W * 0.9, 0.45, L, colour, 0, 0.55, 0, 0.1));
      g.add(box(W * 0.55, 0.4, L * 0.35, GLASS, 0, 0.95, 0.1, 0.1)); // cockpit
      g.add(box(W * 0.6, 0.3, 0.5, colour, 0, 0.9, -L * 0.2, 0.06)); // engine hump
      g.add(box(W * 1.1, 0.08, 0.5, DARK, 0, 1.35, -L / 2 + 0.2, 0.02)); // spoiler
      g.add(box(0.08, 0.5, 0.3, DARK, -W * 0.4, 1.05, -L / 2 + 0.2, 0.02));
      g.add(box(0.08, 0.5, 0.3, DARK, W * 0.4, 1.05, -L / 2 + 0.2, 0.02));
      g.add(box(W * 1.1, 0.12, 0.4, DARK, 0, 0.35, L / 2 - 0.1, 0.02)); // front wing
      addWheels(r, 0.45, [L * 0.33, -L * 0.33]);
      const d = driver(0.7);
      d.position.set(0, 0.55, 0.1);
      g.add(d);
      lights(g, L, W, 0.6);
      break;
    }
    case 'truck': {
      r = 0.55;
      g.add(box(W, 0.4, L, DARK, 0, 0.6, 0, 0.05)); // chassis
      g.add(box(W, 1.3, L * 0.4, colour, 0, 1.4, L * 0.28)); // cab
      g.add(box(W * 0.9, 0.7, 0.1, GLASS, 0, 1.6, L * 0.48, 0.03)); // windscreen
      g.add(box(W * 0.96, 0.6, L * 0.55, colour, 0, 1.05, -L * 0.2, 0.05)); // bed
      g.add(box(0.2, 0.6, 0.8, '#b0bec5', -W / 2 - 0.05, 0.9, L * 0.05, 0.03)); // tanks
      g.add(box(0.2, 0.6, 0.8, '#b0bec5', W / 2 + 0.05, 0.9, L * 0.05, 0.03));
      addWheels(r, 0.4, [L * 0.32, -L * 0.28]);
      const d = driver(0.75);
      d.position.set(-0.35, 1.05, L * 0.25);
      g.add(d);
      lights(g, L, W, 0.9);
      break;
    }
    case 'van': {
      r = 0.45;
      g.add(box(W, 1.6, L, colour, 0, 1.25, 0, 0.14));
      g.add(box(W * 0.9, 0.7, 0.1, GLASS, 0, 1.6, L / 2, 0.03));
      g.add(box(0.1, 0.5, L * 0.5, GLASS, -W / 2, 1.6, -L * 0.1, 0.03)); // side windows
      g.add(box(0.1, 0.5, L * 0.5, GLASS, W / 2, 1.6, -L * 0.1, 0.03));
      g.add(box(W * 1.02, 0.25, L * 1.02, DARK, 0, 0.55, 0, 0.03));
      addWheels(r, ww, [L * 0.33, -L * 0.33]);
      const d = driver(0.7);
      d.position.set(-0.35, 0.9, L * 0.28);
      g.add(d);
      lights(g, L, W, 0.9);
      break;
    }
    case 'taxi': {
      g.add(box(W, 0.6, L, colour, 0, 0.75, 0, 0.12));
      g.add(box(W * 0.85, 0.7, L * 0.5, colour, 0, 1.35, -0.1, 0.14));
      g.add(box(W * 0.8, 0.5, 0.1, GLASS, 0, 1.35, L * 0.16, 0.03));
      g.add(box(0.1, 0.4, L * 0.4, GLASS, -W * 0.43, 1.38, -0.1, 0.03));
      g.add(box(0.1, 0.4, L * 0.4, GLASS, W * 0.43, 1.38, -0.1, 0.03));
      g.add(box(0.9, 0.3, 0.3, LIGHT, 0, 1.85, -0.1, 0.05)); // roof sign
      g.add(box(0.6, 0.12, 0.05, DARK, 0, 1.85, 0.07, 0.01));
      g.add(box(W * 1.05, 0.2, L * 1.02, DARK, 0, 0.5, 0, 0.03));
      addWheels(r, ww, [L * 0.33, -L * 0.33]);
      const d = driver(0.65);
      d.position.set(-0.35, 0.85, 0.2);
      g.add(d);
      lights(g, L, W, 0.8);
      break;
    }
    case 'police': {
      g.add(box(W, 0.6, L, colour, 0, 0.75, 0, 0.12));
      g.add(box(W * 0.85, 0.7, L * 0.5, '#f5f5f5', 0, 1.35, -0.1, 0.14));
      g.add(box(W * 0.8, 0.5, 0.1, GLASS, 0, 1.35, L * 0.16, 0.03));
      g.add(box(0.5, 0.25, 0.35, '#e53935', -0.3, 1.82, -0.1, 0.05));
      g.add(box(0.5, 0.25, 0.35, '#1e88e5', 0.3, 1.82, -0.1, 0.05));
      g.add(box(W * 1.05, 0.2, L * 1.02, DARK, 0, 0.5, 0, 0.03));
      g.add(box(W * 1.1, 0.35, 0.3, DARK, 0, 0.6, L / 2, 0.05)); // push bar
      addWheels(r, ww, [L * 0.33, -L * 0.33]);
      const d = driver(0.65);
      d.position.set(-0.35, 0.85, 0.2);
      g.add(d);
      lights(g, L, W, 0.8);
      break;
    }
    case 'bus': {
      r = 0.5;
      g.add(box(W, 2.0, L, colour, 0, 1.5, 0, 0.14));
      g.add(box(W * 0.9, 0.9, 0.1, GLASS, 0, 1.95, L / 2, 0.03));
      for (let i = 0; i < 5; i++) {
        const z = L / 2 - 0.9 - i * (L / 5.2);
        g.add(box(0.1, 0.7, 0.8, GLASS, -W / 2, 1.9, z, 0.03));
        g.add(box(0.1, 0.7, 0.8, GLASS, W / 2, 1.9, z, 0.03));
      }
      g.add(box(W * 1.02, 0.25, L * 1.02, DARK, 0, 0.55, 0, 0.03));
      g.add(box(W * 0.6, 0.3, 0.2, LIGHT, 0, 2.3, L / 2, 0.03)); // destination sign
      addWheels(r, 0.4, [L * 0.35, -L * 0.3]);
      const d = driver(0.7);
      d.position.set(-0.45, 1.05, L * 0.35);
      g.add(d);
      lights(g, L, W, 0.9);
      break;
    }
    case 'tractor': {
      g.add(box(W * 0.7, 0.9, L * 0.9, colour, 0, 1.1, 0.1, 0.1)); // body
      g.add(box(W * 0.5, 0.5, L * 0.45, colour, 0, 1.75, L * 0.22, 0.08)); // bonnet
      g.add(box(0.25, 0.8, 0.25, DARK, W * 0.15, 2.2, L * 0.35, 0.05)); // exhaust
      g.add(box(0.1, 1.0, 0.1, DARK, -W * 0.3, 2.0, -L * 0.2, 0.02)); // roll bar
      g.add(box(0.1, 1.0, 0.1, DARK, W * 0.3, 2.0, -L * 0.2, 0.02));
      g.add(box(W * 0.75, 0.1, 0.9, DARK, 0, 2.5, -L * 0.15, 0.02)); // roof
      addWheels(0.35, 0.3, [L * 0.35], 0.35, W * 0.42);
      addWheels(0.8, 0.5, [-L * 0.25], 0.8, W * 0.5);
      const d = driver(0.7);
      d.position.set(0, 1.2, -L * 0.15);
      g.add(d);
      lights(g, L * 0.9, W * 0.7, 1.7);
      break;
    }
    case 'kart': {
      r = 0.32;
      g.add(box(W * 0.8, 0.2, L, colour, 0, 0.35, 0, 0.05)); // floor pan
      g.add(box(W * 0.5, 0.35, 0.8, colour, 0, 0.6, L * 0.25, 0.06)); // nose
      g.add(box(W * 0.6, 0.6, 0.6, DARK, 0, 0.6, -L * 0.3, 0.05)); // engine/seat back
      g.add(box(W * 1.05, 0.15, 0.15, DARK, 0, 0.45, L / 2, 0.03)); // bumper
      g.add(box(0.06, 0.3, 0.06, DARK, 0, 0.75, L * 0.1, 0.01)); // steering column
      g.add(box(0.5, 0.06, 0.5, DARK, 0, 0.9, L * 0.1, 0.02).rotateX(Math.PI / 2 - 0.8));
      addWheels(r, 0.3, [L * 0.3, -L * 0.32], r, W * 0.55);
      const d = driver(0.75);
      d.position.set(0, 0.35, -0.15);
      g.add(d);
      break;
    }
    case 'monster': {
      r = 1.1;
      g.add(box(W * 0.8, 0.7, L * 0.85, colour, 0, 1.9, 0, 0.12)); // body
      g.add(box(W * 0.7, 0.7, L * 0.4, colour, 0, 2.5, -0.1, 0.14)); // cab
      g.add(box(W * 0.65, 0.5, 0.1, GLASS, 0, 2.5, L * 0.12, 0.03));
      g.add(box(W * 0.8, 0.3, L * 0.9, DARK, 0, 1.45, 0, 0.05)); // frame
      g.add(box(0.15, 0.9, 0.15, '#b0bec5', -W * 0.3, 1.0, L * 0.3, 0.02)); // shocks
      g.add(box(0.15, 0.9, 0.15, '#b0bec5', W * 0.3, 1.0, L * 0.3, 0.02));
      g.add(box(0.15, 0.9, 0.15, '#b0bec5', -W * 0.3, 1.0, -L * 0.3, 0.02));
      g.add(box(0.15, 0.9, 0.15, '#b0bec5', W * 0.3, 1.0, -L * 0.3, 0.02));
      addWheels(r, 0.7, [L * 0.32, -L * 0.32], r, W * 0.5 + 0.2);
      const d = driver(0.7);
      d.position.set(-0.3, 2.0, 0.15);
      g.add(d);
      lights(g, L * 0.85, W * 0.8, 1.9);
      break;
    }
    default: {
      // Car
      g.add(box(W, 0.6, L, colour, 0, 0.75, 0, 0.12)); // body
      g.add(box(W * 0.85, 0.65, L * 0.5, colour, 0, 1.35, -0.2, 0.16)); // cabin
      g.add(box(W * 0.8, 0.45, 0.1, GLASS, 0, 1.35, L * 0.06, 0.03)); // windscreen
      g.add(box(W * 0.8, 0.4, 0.1, GLASS, 0, 1.35, -L * 0.46, 0.03)); // rear window
      g.add(box(0.1, 0.38, L * 0.4, GLASS, -W * 0.43, 1.38, -0.2, 0.03));
      g.add(box(0.1, 0.38, L * 0.4, GLASS, W * 0.43, 1.38, -0.2, 0.03));
      g.add(box(W * 1.05, 0.2, L * 1.02, DARK, 0, 0.5, 0, 0.03)); // underbody
      g.add(box(W * 1.06, 0.25, 0.25, '#cfd8dc', 0, 0.55, L / 2, 0.05)); // bumpers
      g.add(box(W * 1.06, 0.25, 0.25, '#cfd8dc', 0, 0.55, -L / 2, 0.05));
      addWheels(r, ww, [L * 0.33, -L * 0.33]);
      const d = driver(0.62);
      d.position.set(-0.35, 0.85, 0.1);
      g.add(d);
      lights(g, L, W, 0.8);
    }
  }
  return { root: g, wheels };
}

export function buildBlockyTree(rng: () => number): THREE.Object3D {
  const g = new THREE.Group();
  const h = 1.5 + rng() * 2;
  const greens = ['#43a047', '#2e7d32', '#66bb6a', '#7cb342'];
  g.add(box(0.7, h, 0.7, '#6d4c41', 0, h / 2, 0, 0.05));
  const c1 = 2 + rng() * 1.5;
  g.add(box(c1, c1, c1, greens[Math.floor(rng() * greens.length)]!, 0, h + c1 / 2 - 0.3, 0, 0.15));
  if (rng() > 0.5) {
    const c2 = c1 * 0.6;
    g.add(box(c2, c2, c2, greens[Math.floor(rng() * greens.length)]!, 0, h + c1 + c2 / 2 - 0.6, 0, 0.12));
  }
  return g;
}

export function buildBlockyBuilding(rng: () => number): THREE.Object3D {
  const g = new THREE.Group();
  const w = 6 + rng() * 8;
  const d = 6 + rng() * 8;
  const floors = 2 + Math.floor(rng() * 6);
  const h = floors * 3;
  const colours = ['#ef9a9a', '#90caf9', '#fff59d', '#a5d6a7', '#ffcc80', '#ce93d8', '#eeeeee'];
  g.add(box(w, h, d, colours[Math.floor(rng() * colours.length)]!, 0, h / 2, 0, 0.1));
  g.add(box(w + 0.4, 0.4, d + 0.4, DARK, 0, h + 0.2, 0, 0.05)); // roof trim
  const win = mat('#37474f');
  const winGeo = boxGeo(1.0, 1.2, 0.1, 0.02);
  for (let f = 0; f < floors; f++) {
    const y = f * 3 + 1.7;
    for (let x = -w / 2 + 1.2; x < w / 2 - 0.8; x += 2) {
      for (const side of [1, -1]) {
        const m = new THREE.Mesh(winGeo, win);
        m.position.set(x, y, (side * (d + 0.05)) / 2);
        g.add(m);
      }
    }
    for (let z = -d / 2 + 1.2; z < d / 2 - 0.8; z += 2) {
      for (const side of [1, -1]) {
        const m = new THREE.Mesh(winGeo, win);
        m.position.set((side * (w + 0.05)) / 2, y, z);
        m.rotation.y = Math.PI / 2;
        g.add(m);
      }
    }
  }
  return g;
}

let studTexture: THREE.CanvasTexture | null = null;
export function studTex(): THREE.CanvasTexture {
  if (studTexture) return studTexture;
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#5ab84a';
  ctx.fillRect(0, 0, 64, 64);
  // stud: darker shadow ring under a lighter disc
  ctx.fillStyle = '#4a9d3d';
  ctx.beginPath();
  ctx.arc(32, 35, 17, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#6cc75a';
  ctx.beginPath();
  ctx.arc(32, 31, 16, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#7fd66c';
  ctx.beginPath();
  ctx.arc(32, 29, 10, 0, Math.PI * 2);
  ctx.fill();
  studTexture = new THREE.CanvasTexture(c);
  studTexture.wrapS = studTexture.wrapT = THREE.RepeatWrapping;
  studTexture.repeat.set(300, 300);
  studTexture.magFilter = THREE.NearestFilter;
  studTexture.colorSpace = THREE.SRGBColorSpace;
  studTexture.anisotropy = 4;
  return studTexture;
}

export const blockyStyle: StyleRenderer = {
  id: 'blocky',
  buildVehicle: (def, colour) => Promise.resolve(buildBlockyVehicle(def, colour)),
  buildTree: (rng) => Promise.resolve(buildBlockyTree(rng)),
  buildBuilding: (rng) => Promise.resolve(buildBlockyBuilding(rng)),
  groundMaterial: () => new THREE.MeshLambertMaterial({ map: studTex() }),
  roadMaterial: () => mat('#4a5560'),
};
